/**
 * Web push subscribe/unsubscribe (docs/plan/api.md `PUT`/`DELETE /push/device`, docs/plan/notifications.md
 * "Opt-in"): the standard Push API through `/sw.js` (public/sw.js), no Firebase. Kept dependency-free, like the
 * service worker itself.
 */
import type { ApiClient } from '../../api/client'
import type { PushCategory, PushDeviceRequest } from '../../api/types'
import { readCache, writeCache } from '../../lib/offlineCache'
import { APP_VERSION } from '../../lib/version'

export const SERVICE_WORKER_PATH = '/sw.js'

const INSTALLATION_ID_KEY = 'macrotide.push.installationId'
const CATEGORIES_KEY = 'macrotide.push.categories'

/** A stable id for this browser, made up the first time it's needed and kept from then on. */
export function readInstallationId(): string {
  const existing = readCache<string>(INSTALLATION_ID_KEY)
  if (existing) return existing
  const id = crypto.randomUUID()
  writeCache(INSTALLATION_ID_KEY, id)
  return id
}

export function readPushCategories(): PushCategory[] {
  return readCache<PushCategory[]>(CATEGORIES_KEY) ?? []
}

export function writePushCategories(categories: readonly PushCategory[]): void {
  writeCache(CATEGORIES_KEY, categories)
}

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window
}

/** The VAPID public key (`/config`'s `vapidPublicKey`, URL-safe base64) as the raw bytes `pushManager.subscribe`'s
 * `applicationServerKey` needs. A plain `ArrayBuffer`-backed array: `Uint8Array.from` alone types as the wider
 * `ArrayBufferLike` (which also covers `SharedArrayBuffer`), which `BufferSource` doesn't accept. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/** Registers `/sw.js` (idempotent: a second call just returns the existing registration) and subscribes, asking
 * for the browser's notification permission first. Call only from the user's own click (Settings → Notifications'
 * switch): browsers refuse a permission prompt that isn't. */
export async function subscribeBrowser(vapidPublicKey: string): Promise<PushSubscriptionJSON> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notifications are blocked for this site in your browser.')
  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH)
  const existing = await registration.pushManager.getSubscription()
  const subscription = existing ?? (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) }))
  return subscription.toJSON()
}

/** `PUT /push/device`'s body from a browser subscription; throws if the browser left out a field it promises. */
export function devicePayload(installationId: string, subscription: PushSubscriptionJSON, categories: readonly PushCategory[]): PushDeviceRequest {
  const keys = subscription.keys
  if (!subscription.endpoint || !keys?.p256dh || !keys.auth) {
    throw new Error('The browser did not return a usable push subscription.')
  }
  return {
    installationId,
    platform: 'web',
    webSubscription: { endpoint: subscription.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } },
    categories: [...categories],
    appVersion: APP_VERSION,
  }
}

/** Unsubscribes this browser's push subscription, if it has one; a no-op where push was never set up. */
export async function unsubscribeBrowser(): Promise<void> {
  if (!pushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
  const subscription = await registration?.pushManager.getSubscription()
  await subscription?.unsubscribe()
}

/** Sign-out and account deletion both forget this browser's device (docs/plan/notifications.md "Devices"): best
 * effort, since the user is leaving either way and the server also drops a device unseen for 270 days. */
export async function clearPushSubscription(api: Pick<ApiClient, 'unregisterPushDevice'>): Promise<void> {
  try {
    await unsubscribeBrowser()
    await api.unregisterPushDevice(readInstallationId())
  } catch {
    // Best effort, as above.
  }
}
